import axios from "axios";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import useSticky from "../../Utility/useSticky";

function CapacityPricing({ onSuccess, onBack, experienceId }) {
  const token = localStorage.getItem("authToken");
  
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setloadingSubmit] = useState(false);

  const [currency, setCurrency] = useState({
    id: 6, // Default Australia
    country_name: "Australia",
    currency: "AUD",
    symbol: "$",
  });
  const selectedCurrency = JSON.parse(localStorage.getItem("selectedCurrency"));

  const location = useLocation();
  
  const updateexperienceId = location.state?.experienceId;
  const isEdit = location.state?.isEdit;
  const [uploadingImages, setUploadingImages] = useState({});

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const signupStatus = user.signup_complete_status;

  const activityPlaceRefs = useRef([]);
  const activityTitleRefs = useRef([]);
  const timeRefs = useRef([]);

  const [formData, setFormData] = useState({
    no_of_guest: "",
    price: "",
  });

  const guestRef = useRef(null);
  const priceRef = useRef(null);

  // Activities state
  const [activities, setActivities] = useState([
    { activity_title: "", place: "", activity_time: "", imageFile: null },
  ]);

  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedPackageTitle, setSelectedPackageTitle] = useState("");

  const [packages, setPackages] = useState([]);

  const [activitiesByPackage, setActivitiesByPackage] = useState({});

  const isSticky = useSticky("sticy_fixedss1", 130);

  // ✅ Fetch Packages by Experience ID
  const fetchPackages = async () => {
    try {
      const expId = experienceId || updateexperienceId;
      if (!expId) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/host/get-packages/${expId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status && Array.isArray(res.data?.data)) {
        setPackages(res.data.data);
      } else {
        setPackages([]);
      }
    } catch (err) {
      console.error("Error fetching packages:", err);
    }
  };

 
  // ✅ 1. Fetch packages first
  useEffect(() => {
    if (experienceId || updateexperienceId) {
      fetchPackages();
    }
  }, [experienceId, updateexperienceId]);

  // ✅ 2. Once packages are fetched → select first package & load its itinerary
  useEffect(() => {
    if (packages.length > 0 && !selectedPackageId) {
      const firstPkg = packages[0];
      setSelectedPackageId(firstPkg.id);
      setSelectedPackageTitle(firstPkg.title);
      fetchItineraryData(firstPkg.id);
    }
  }, [packages]);

  const fetchItineraryData = async (packageId) => {
    try {
      const expId = experienceId || updateexperienceId;
      if (!expId) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/host/get-host-itinerary/${expId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = res.data?.data || [];
      const baseUrl = res.data?.base_url || "";

      const grouped = data.reduce((acc, item) => {
        if (!acc[item.package_id]) acc[item.package_id] = [];
        acc[item.package_id].push({
          activity_title: item.activity_title || "",
          place: item.place || "",
          activity_time: item.activity_time || "",
          image: item.image ? `${baseUrl}${item.image}` : "",
          rawImagePath: item.image || "",
          package_id: item.package_id,
        });
        return acc;
      }, {});

      setActivitiesByPackage(grouped);

      const existingActivities = grouped[packageId] || [];
      if (existingActivities.length > 0) {
        setActivities(existingActivities);
      } else {
        // ✅ Ensure the default package_id is assigned here
        setActivities([
          {
            activity_title: "",
            place: "",
            activity_time: "",
            imageFile: null,
            package_id: packageId,
          },
        ]);
      }

      // ✅ Also explicitly update selectedPackageId right after fetching
      setSelectedPackageId(packageId);
    } catch (error) {
      console.error("Error fetching itinerary data:", error);
    }
  };

  useEffect(() => {
    if (experienceId) {
      fetchExistingCapacity();
    }
  }, [experienceId]);

  // ✅ Load currency on mount
  useEffect(() => {
    if (selectedCurrency) {
      setCurrency({
        id: selectedCurrency.id,
        country_name: selectedCurrency.country_name,
        currency: selectedCurrency.currency,
        symbol: selectedCurrency.symbol,
      });
    }
  }, []);

  const deleteImage = async (imagePath) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/host/delete-image`,
        { image_path: imagePath },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return true;
    } catch (err) {
      console.error("Failed to delete image:", err);

      return true;
    }
  };

  const handleActivityImageUpload = async (index, file, e) => {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, and PNG files are allowed");
      e.target.value = null;
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size must be ≤ 2MB");
      e.target.value = null;
      return;
    }

    try {
      // ✅ if old image exists, delete it first
      if (activities[index].image) {
        const deleted = await deleteImage(activities[index].rawImagePath);
        if (!deleted) return; // stop if failed
      }

      setUploadingImages((prev) => ({ ...prev, [index]: true }));
      const formData = new FormData();
      formData.append("image", file);

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/host/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // const imagePath = res.data?.data?.image_path || "";

      // const updated = [...activities];
      // updated[index].image = imagePath;
      // setActivities(updated);

      const imagePath = res.data?.data?.image_path || "";
      const baseUrl = res.data?.base_url || "";

      const updated = [...activities];
      // ✅ store full URL for preview
      updated[index].image = `${baseUrl}${imagePath}`;
      // ✅ also keep raw path for delete API later
      updated[index].rawImagePath = imagePath;

      setActivities(updated);

      // ✅ Update activitiesByPackage for this package
      setActivitiesByPackage((prev) => ({
        ...prev,
        [selectedPackageId]: updated,
      }));

      toast.success("Activity image uploaded");
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploadingImages((prev) => ({ ...prev, [index]: false })); // ✅ Stop loader
    }
  };

  // const handleActivityChange = (index, field, value) => {
  //   const updated = [...activities];
  //   updated[index][field] = value;
  //   setActivities(updated);

  //   // also update in activitiesByPackage
  //   setActivitiesByPackage(prev => ({
  //     ...prev,
  //     [selectedPackageId]: updated
  //   }));
  // };

  // Add activity

  const handleActivityChange = (index, field, value) => {
    const updated = [...activities];
    updated[index][field] = value;
    updated[index].package_id = selectedPackageId; // 🟢 ensures package_id is always attached
    setActivities(updated);

    setActivitiesByPackage((prev) => ({
      ...prev,
      [selectedPackageId]: updated,
    }));
  };

  const addActivity = () => {
    const newActivity = {
      activity_title: "",
      place: "",
      activity_time: "",
      imageFile: null,
      package_id: selectedPackageId,
    };

    setActivitiesByPackage((prev) => {
      const existing = prev[selectedPackageId] || [];
      const updated = [...existing, newActivity];
      return { ...prev, [selectedPackageId]: updated };
    });

    // reflect in current activities state
    setActivities((prev) => [...prev, newActivity]);
  };

  // Remove activity
  const removeActivity = (index) => {
    const updated = [...activities];
    updated.splice(index, 1);
    setActivities(updated);
  };

  const fetchExistingCapacity = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/host/get-host-experience/${experienceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Adjust according to your API response structure:
      // Example response:
      // {
      //   data: {
      //     no_of_guest: 10,
      //     price: 500
      //   }
      // }

      const data = res.data?.data;

      if (data) {
        setFormData({
          no_of_guest: data.no_of_guest || "",
          price: data.price || "",
          symbol: data.currency_symbol || "",
        });
      }
    } catch (err) {
      console.error("Error fetching existing guest info:", err);
      // toast.error("Failed to load existing guest info");
    }
  };

  // handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ Validation function
  const validateForm = () => {
    for (let i = 0; i < activities.length; i++) {
      const { place, activity_title, activity_time } = activities[i]; // ✅ destructure

      if (!place) {
        toast.error(`Activity ${i + 1}: Title is required`);
        activityPlaceRefs.current[i]?.focus();
        return false;
      }
      if (!activity_title) {
        toast.error(`Activity ${i + 1}: Description is required`);
        activityTitleRefs.current[i]?.focus();
        return false;
      }
      //     if (!activity_time) {
      //       toast.error(`Activity ${i + 1}: Time is required`);
      //       return false;
      //     }

      //     if (!activities[i].image) {
      //   toast.error(`Activity ${i + 1}: Image is required`);
      //   return false;
      // }

      // Validate sequential time
      // if (i > 0) {
      //   const prevTime = activities[i - 1].activity_time;
      //   if (activity_time <= prevTime) {
      //     toast.error(`Activity ${i + 1}: Time must be after previous activity (${prevTime})`);

      //     return false;
      //   }
      // }
    }

    return true; // ✅ All good
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setloadingSubmit(true);

    try {
      // ✅ Step 1: Check if there are any packages
      if (packages.length === 0) {
        toast.error("Please add at least one package before proceeding!");
        setloadingSubmit(false);
        return;
      }

      // ✅ Step 2: Check if every package has at least one valid itinerary
      for (const pkg of packages) {
        const pkgId = pkg.id;
        const pkgTitle = pkg.title || "Untitled Package";
        const pkgActivities = activitiesByPackage[pkgId] || [];

        if (pkgActivities.length === 0) {
          toast.error(`Please fill at least one itinerary for "${pkgTitle}"`);
          setloadingSubmit(false);
          return;
        }

        // ✅ Step 3: Validate each activity inside that package
        for (let i = 0; i < pkgActivities.length; i++) {
          const act = pkgActivities[i];
          if (!act.place?.trim()) {
            toast.error(`"${pkgTitle}" → Activity ${i + 1}: Title is required`);
            setloadingSubmit(false);
            return;
          }
          if (!act.activity_title?.trim()) {
            toast.error(
              `"${pkgTitle}" → Activity ${i + 1}: Description is required`
            );
            setloadingSubmit(false);
            return;
          }
          // if (!act.activity_time) {
          //   toast.error(`"${pkgTitle}" → Activity ${i + 1}: Time is required`);
          //   setloadingSubmit(false);
          //   return;
          // }
        }
      }

      // ✅ Step 4: All validations passed
      setTimeout(() => {
        toast.success("All packages have valid itineraries");
        if (onSuccess) onSuccess();
      }, 1000);
    } catch (error) {
      console.error("Validation error:", error);
      // toast.error("Something went wrong during validation");
    } finally {
      setloadingSubmit(false);
    }
  };

  const handleUpdateItinerary = async () => {
    if (!validateForm()) return;

    if (!selectedPackageId) {
      toast.error("Please select a package first!");
      return;
    }

    try {
      setLoading(true);

      // 🟢 Get all activities of the selected package
      const pkgActivities = activities.filter(
        (a) => a.package_id === selectedPackageId
      );

      if (pkgActivities.length === 0) {
        toast.error("Please add at least one activity for this package!");
        return;
      }

      // 🟢 Prepare payload for the selected package only
      const itineraryPayload = {
        experience_id: experienceId || updateexperienceId,
        package_id: selectedPackageId,
        activity_time: pkgActivities.map((a) => a.activity_time),
        place: pkgActivities.map((a) => a.place),
        activity_title: pkgActivities.map((a) => a.activity_title),
        image: pkgActivities.map((a) => a.rawImagePath || ""),
      };

      // 🟢 Save data immediately to API
      await toast.promise(
        axios.post(
          `${process.env.REACT_APP_API_BASE}/host/add-itinerary`,
          itineraryPayload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        {
          loading: `Saving itinerary for ${selectedPackageTitle}...`,
          success: `${selectedPackageTitle} itinerary saved`,
          error: "Failed to save itinerary",
        }
      );

      // 🟢 Keep saved activities in memory so you can switch back later
      setActivitiesByPackage((prev) => ({
        ...prev,
        [selectedPackageId]: pkgActivities,
      }));
    } catch (err) {
      console.error("Error saving itinerary:", err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`frm_suport_als mng_bothss_li ${
        isSticky ? "manges_bx_areea" : ""
      }`}
    >
      <div className="row">
        <div
          className={`col-lg-4 col-sm-5 ${
            packages.length > 0 ? "active-class" : ""
          } ${isSticky ? "fixedss" : ""}`}
          id="sticy_fixedss"
        >
          <div className="pakeges_ulli">
            {packages.length > 0 ? (
              packages.map((pkg) => (
                <div key={pkg.id} className="tb_btnss">
                  <button
                    type="button"
                    className={`al_sp_btn_blkss ${
                      selectedPackageId === pkg.id ? "actact" : ""
                    }`}
                    onClick={() => {
                      setSelectedPackageId(pkg.id);
                      setSelectedPackageTitle(pkg.title);

                      // ✅ Load activities for this package if already fetched
                      if (activitiesByPackage[pkg.id]) {
                        // ensure clean structure (no leftover image paths)
                        setActivities(
                          activitiesByPackage[pkg.id].map((a) => ({
                            ...a,
                            image: a.image || "",
                            rawImagePath: a.rawImagePath || "",
                            imageFile: null,
                            package_id: pkg.id,
                          }))
                        );
                      } else {
                        // ✅ Fresh blank form for a new package
                        setActivities([
                          {
                            activity_title: "",
                            place: "",
                            activity_time: "",
                            image: "",
                            rawImagePath: "",
                            imageFile: null,
                            package_id: pkg.id,
                          },
                        ]);
                      }

                      // Always fetch latest itinerary from server (ensures sync)
                      fetchItineraryData(pkg.id);
                    }}
                  >
                    {pkg.title || "Untitled Package"}
                  </button>
                </div>
              ))
            ) : (
              <p className="text-muted">No packages found</p>
            )}
          </div>
        </div>
        <div className="col-lg-6  col-sm-7">
          <form onSubmit={handleSubmit}>
            <div className="titlsss form_al_fildss">
              <div className="ar_bothss">
                <h5>Add Itinerary</h5>
                <button
                  type="button"
                  className="al_sp_btn_blk"
                  onClick={addActivity}
                >
                  <img alt="" src="/images/pls_adss.png" />
                </button>
              </div>

              <div className="ads_frm_areea bx_blankss">
                {activities.map((activity, index) => (
                  <div
                    className="ads_frm_areea"
                    key={`${selectedPackageId}-${index}`}
                  >
                    <div className="ar_bothss">
                      <h5>Add itinerary</h5>
                      {activities.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger remove-btn"
                          onClick={() => removeActivity(index)}
                        >
                          <img alt="" src="/images/delates.png" />
                        </button>
                      )}
                    </div>

                    <div key={index}>
                      <div className="form-group">
                        <label>
                          Add Title for your activity
                          <span className="read_tx">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Itinerary a title"
                          maxLength={200}
                          value={activity.place}
                          // onChange={(e) =>
                          //   handleActivityChange(index, "place", e.target.value)
                          // }
                          onChange={(e) => {
                            const val = e.target.value.slice(0, 150);
                            handleActivityChange(index, "place", val);
                          }}
                          ref={(el) => (activityPlaceRefs.current[index] = el)}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          Describe what guests will do
                          <span className="read_tx">*</span>
                        </label>
                        <textarea
                          type="text"
                          className="form-control"
                          placeholder="Enter description"
                          rows="4"
                          value={activity.activity_title}
                          onChange={(e) =>
                            handleActivityChange(
                              index,
                              "activity_title",
                              e.target.value
                            )
                          }
                          maxLength={250}
                          ref={(el) => (activityTitleRefs.current[index] = el)}
                        />
                      </div>

                      <div className="form-group">
                        <label>Time</label>
                        <div className="row">
                          <div className="col-lg-12">
                            <input
                              type="time"
                              className="form-control"
                              value={activity.activity_time}
                              min={
                                index > 0
                                  ? activities[index - 1].activity_time
                                  : undefined
                              } // Sequential time
                              ref={(el) => (timeRefs.current[index] = el)} // 👈 save ref in array
                              onFocus={() =>
                                timeRefs.current[index]?.showPicker?.()
                              }
                              onChange={(e) =>
                                handleActivityChange(
                                  index,
                                  "activity_time",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Activity Image </label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={(e) =>
                            handleActivityImageUpload(
                              index,
                              e.target.files[0],
                              e
                            )
                          }
                        />
                        {uploadingImages[index] && (
                          <div className="loader"></div>
                        )}

                        {activity.image && (
                          <div className="ad_imagesss">
                            <img
                              src={activity.image}
                              alt="Activity"
                              className="mg_areea"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="form-group mt-1 text-center">
                  <button
                    type="button"
                    className="btn_allls over mr-0"
                    onClick={handleUpdateItinerary}
                    disabled={loading}
                  >
                    {/* {loading ? "Saving..." : "Update"} */}
                    {loading
                      ? isEdit
                        ? "Updating..."
                        : "Saving..."
                      : isEdit
                      ? "Update"
                      : "Save"}
                  </button>
                </div>
              </div>
            </div>

            <div className="titlsss form_al_fildss">
              {/* <div className="ar_bothss">
			<h5>Add more photos of the experience</h5>
	
			<button type="button" className="al_sp_btn_blk" onClick={addImage}>
               <img alt="" src="/images/pls_adss.png" />
            </button>  

			


		   </div>
		
		
				{images.map((img, index) => (
            <div className="form-group" key={index}>
              <label>Photo <span className="read_tx">*</span></label>
              <div className="bothads">
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) =>
                    handleImageChange(index, "file", e.target.files[0],e)
                  }
                  ref={(el) => (imageFileRefs.current[index] = el)}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Add priority"
                  value={img.priority}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                    }
                  }}
                  onChange={(e) =>
                    handleImageChange(index, "priority", e.target.value)
                  }
                  ref={(el) => (imagePriorityRefs.current[index] = el)}
                  maxLength={2}
                />


                {images.length > 1 && (
      <span
        className="btnss remove"
        onClick={() => removeImage(index)}
      >
        <img alt="" src="/images/delates.png" />
      </span>
    )}

              </div>
              {img.previewUrl && (
			  <div className="add_mgs_area">
  <img
    src={img.previewUrl}
    alt="preview"
  />
  </div>
)}

            </div>
          ))} */}

              <div className="form-group both">
                <button
                  type="button"
                  className="btn_allls over"
                  value=""
                  onClick={onBack}
                >
                  Back
                </button>
                <button type="submit" className="btn_allls" disabled={loading}>
                  {loadingSubmit ? "Saving..." : "Next"}
                  {/* {loadingSubmit ? (
                    <>
                      <span className="spinner"></span>
                    </>
                  ) : (
                    "Next"
                  )} */}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CapacityPricing;
